import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/contexts/AuthContext';

interface ClientUser {
  id: string;
  user_id: string;
  role: string;
  is_active: boolean;
  created_at: string;
  profile: {
    full_name: string;
    email: string;
    phone: string | null;
  } | null;
}

interface Profile {
  id: string;
  full_name: string;
  email: string;
}

interface ClientUsersTabProps {
  clientId: string;
}

export default function ClientUsersTab({ clientId }: ClientUsersTabProps) {
  const { profile } = useAuthContext();
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [allProfiles, setAllProfiles] = useState<Profile[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);

    // Buscar client_users
    const { data: cuData } = await supabase
      .from('client_users')
      .select('id, user_id, role, is_active, created_at')
      .eq('client_id', clientId)
      .order('created_at');

    if (!cuData || cuData.length === 0) {
      setUsers([]);
      setLoading(false);
      return;
    }

    // Buscar perfis separadamente
    const userIds = cuData.map((cu) => cu.user_id);
    const { data: profilesData } = await supabase
      .from('profiles')
      .select('id, full_name, email, phone')
      .in('id', userIds);

    const profileMap = Object.fromEntries(
      (profilesData || []).map((p) => [p.id, p])
    );

    const enriched: ClientUser[] = cuData.map((cu) => ({
      ...cu,
      profile: profileMap[cu.user_id] || null,
    }));

    setUsers(enriched);
    setLoading(false);
  }, [clientId]);

  const loadProfiles = useCallback(async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .eq('is_active', true)
      .order('full_name');
    setAllProfiles(data || []);
  }, []);

  useEffect(() => {
    loadUsers();
    loadProfiles();
  }, [loadUsers, loadProfiles]);

  const handleAdd = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    const { error } = await supabase.from('client_users').insert({
      client_id: clientId,
      user_id: selectedUserId,
      role: selectedRole,
      created_by: profile?.id,
    });
    if (!error) {
      // Atualizar client_id no profile
      await supabase.from('profiles').update({ client_id: clientId }).eq('id', selectedUserId);
      loadUsers();
      setShowAddModal(false);
      setSelectedUserId('');
      setSelectedRole('viewer');
    }
    setSaving(false);
  };

  const handleToggle = async (cu: ClientUser) => {
    await supabase
      .from('client_users')
      .update({ is_active: !cu.is_active })
      .eq('id', cu.id);
    loadUsers();
  };

  const handleRemove = async (cu: ClientUser) => {
    await supabase.from('client_users').delete().eq('id', cu.id);
    loadUsers();
  };

  const linkedUserIds = users.map((u) => u.user_id);
  const availableProfiles = allProfiles.filter((p) => !linkedUserIds.includes(p.id));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-slate-500 text-sm">{users.length} usuário{users.length !== 1 ? 's' : ''} vinculado{users.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          Vincular Usuário
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-xl"></i>
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
          <i className="ri-team-line text-4xl mb-3"></i>
          <p className="text-sm">Nenhum usuário vinculado</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Usuário</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Papel</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Status</th>
                <th className="text-right px-4 py-3 text-slate-500 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((cu) => (
                <tr key={cu.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-cyan-100 shrink-0">
                        <i className="ri-user-line text-cyan-600 text-xs"></i>
                      </div>
                      <div>
                        <p className="text-slate-800 font-medium">{cu.profile?.full_name || '—'}</p>
                        <p className="text-slate-400 text-xs">{cu.profile?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      {cu.role === 'viewer' ? 'Visualizador'
                        : cu.role === 'editor' ? 'Editor'
                        : cu.role === 'client' ? 'Cliente'
                        : cu.role === 'member' ? 'Membro'
                        : cu.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cu.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {cu.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleToggle(cu)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-colors cursor-pointer"
                        title={cu.is_active ? 'Desativar' : 'Ativar'}
                      >
                        <i className={`text-sm ${cu.is_active ? 'ri-toggle-line' : 'ri-toggle-fill'}`}></i>
                      </button>
                      <button
                        onClick={() => handleRemove(cu)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        title="Remover vínculo"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-slate-800 font-semibold">Vincular Usuário</h3>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Usuário</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer"
                >
                  <option value="">Selecionar usuário...</option>
                  {availableProfiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name} — {p.email}</option>
                  ))}
                </select>
                {availableProfiles.length === 0 && (
                  <p className="text-xs text-slate-400 mt-1">Todos os usuários já estão vinculados</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Papel</label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer"
                >
                  <option value="viewer">Visualizador</option>
                  <option value="editor">Editor</option>
                  <option value="client">Cliente</option>
                  <option value="member">Membro</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer whitespace-nowrap">Cancelar</button>
              <button
                onClick={handleAdd}
                disabled={!selectedUserId || saving}
                className="flex items-center gap-2 px-5 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                {saving && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                Vincular
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
